import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  TextField,
  Autocomplete,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { Clear, Print as PrintIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { connect } from "react-redux";
import debounce from "lodash.debounce";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PrintsSearch3 from "../../../components/PrintSearch3";
import CSVLinkWrapper from "../../../components/CSVLinkWrapper";
import PATClearButton from "../../../components/Buttons/PATClearButton";
import FilterToolbar from "../../../components/FilterToolbar";
import { areAllPropsEmpty, renderCodeOption } from "../../../utils/functions";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import FilterButtons from "../../../components/Buttons/FilterButtons";
import { screenNames } from "../../../utils/constants";
import SelectFilterDialog from "../../../components/SelectFilterDialog";
import SaveFilterDialog from "../../../components/SaveFilterDialog";
import { formatSaveDateFrom, formatSaveDateTo } from "../../../reducers/TimeReducer";

const headers = [
  { key: "courseDate", label: "Date", format: "date" },
  { key: "course", label: "Course" },
  { key: "employeeName", label: "Employee" },
  { key: "courseLocation", label: "Course Location" },
  { key: "hours", label: "Hours" },
  { key: "category", label: "Category" },
];

const columnsForPrint = headers.map((h) => ({
  field: h.key,
  headerName: h.label,
  editable: false,
  flex: 1,
  ...h,
}));

const filterFieldOptions = [
  { label: "Category", value: "Category" },
  { label: "Comments", value: "Comments" },
  { label: "Grade", value: "Grade" },
  { label: "Hours", value: "Hours" },
];

const filterByTimeOptions = [
  { label: "All", value: "All" },
  { label: "30 Days", value: "30 Days" },
  { label: "1 Month", value: "1 Month" },
  { label: "1 Year", value: "1 Year" },
];

const ParticipantTableToolbar = ({
  participants,
  keyboard,
  onAddClick,
  onFilterChange,
  filters,
  resetFilters,
  employeeOptions,
  courseOptions,
  locationOptions,
  tagTypeOptions,
}) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [printFlag, setPrintFlag] = useState(false);
  const [kbdKey, setKbdKey] = useState(null);
  const [localFilters, setLocalFilters] = useState(filters);
  const [transformedParticipants, setTransformedParticipants] = useState([]);
  const [openSelectFilterDialog, setOpenSelectFilterDialog] = useState(false);
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);

  const isFilterEmpty = areAllPropsEmpty(localFilters);

  const participantPermission = getAccessPermission(
    "training",
    "Participants",
    "any"
  );
  const canAddParticipant = !!participantPermission?.Create;

  useEffect(() => {
    const transformed = (participants || []).map((p) => {
      const details = p?.employeeDetails;

      let employeeName = "";
      if (details && typeof details === "object") {
        const firstName = details.firstName || "";
        const lastName = details.lastName || "";
        const middleName = details.middleName || "";

        employeeName = [lastName, firstName, middleName]
          .filter((n) => typeof n === "string" && n.trim() !== "")
          .join(" ")
          .trim();
      }

      return {
        ...p,
        employeeName,
      };
    });

    setTransformedParticipants(transformed);
  }, [participants]);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilterChange = useCallback(
    debounce((newFilters) => {
      onFilterChange(newFilters);
    }, 500),
    [onFilterChange]
  );

  const handleFilterChange = useCallback(
    (field, value) => {
      let updatedFilters = { ...localFilters, [field]: value };

      if (field === "filterByTime") {
        const today = moment();
        const selectedOption = value;
        updatedFilters.courseDateTo = today.toISOString();

        switch (selectedOption?.value) {
          case "30 Days":
            updatedFilters.courseDateFrom = moment(today)
              .subtract(30, "days")
              .toISOString();
            break;
          case "1 Month":
            updatedFilters.courseDateFrom = moment(today)
              .subtract(1, "months")
              .toISOString();
            break;
          case "1 Year":
            updatedFilters.courseDateFrom = moment(today)
              .subtract(1, "years")
              .toISOString();
            break;
          case "All":
            updatedFilters.courseDateFrom = null;
            updatedFilters.courseDateTo = null;
            break;
          default:
            updatedFilters.courseDateFrom = null;
            updatedFilters.courseDateTo = null;
            break;
        }
      }

      setLocalFilters(updatedFilters);
      debouncedFilterChange(updatedFilters);
    },
    [debouncedFilterChange, localFilters]
  );

  useEffect(() => {
    const subscription = keyboard.subject.subscribe((key) => {
      const validKeys = ["F5", "Ctrl+KeyP", "Ctrl+KeyL"];
      if (validKeys.includes(key)) setKbdKey(key);
    });
    return () => subscription.unsubscribe();
  }, [keyboard]);

  const handlePrintClick = () => {
    setPrintFlag(true);
    setTimeout(() => setPrintFlag(false), 1000);
  };

  const renderFilterFields = (fields) =>
    fields.map(
      ({ type, label, field, options, xs = 2, multiselect = true }) => (
        <Grid item xs={isSmall ? 12 : xs} key={field}>
          {type === "text" && (
            <TextField
              label={label}
              value={localFilters[field] || ""}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          )}

          {type === "date" && (
            <DatePicker
              label={label}
              value={localFilters[field] ? moment(localFilters[field]) : null}
              onChange={(date) =>
                handleFilterChange(field, date ? field === "courseDateFrom" ? formatSaveDateFrom(date) : formatSaveDateTo(date) : null)
              }
              minDate={
                field === "courseDateTo" && localFilters.courseDateFrom
                  ? moment(localFilters.courseDateFrom)
                  : undefined
              }
              maxDate={
                field === "courseDateFrom" && localFilters.courseDateTo
                  ? moment(localFilters.courseDateTo)
                  : undefined
              }
              clearable
              clearText="Clear"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {localFilters[field] && (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleFilterChange(field, null)}
                              size="small"
                              edge="end"
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          {type === "autocomplete" && (
            <Autocomplete
              multiple={multiselect}
              options={options || []}
              value={
                multiselect
                  ? localFilters[field] || []
                  : localFilters[field] || null
              }
              onChange={(_, value) => handleFilterChange(field, value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              renderOption={renderCodeOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  size="small"
                />
              )}
              fullWidth
              sx={
                multiselect
                  ? {
                      "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "nowrap",
                        maxHeight: "40px",
                      },
                      "& .MuiAutocomplete-tag": {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        alignSelf: "flex-start",
                      },
                    }
                  : {}
              }
            />
          )}

          {type === "checkbox" && (
            <Box display="flex" alignItems="center" height="100%">
              <label>
                <input
                  type="checkbox"
                  checked={!!localFilters[field]}
                  onChange={(e) => handleFilterChange(field, e.target.checked)}
                />{" "}
                {label}
              </label>
            </Box>
          )}
        </Grid>
      )
    );

  const renderOptions = () => {
    return (
      <>
        {isSmall ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid item>
              <CSVLinkWrapper
                data={transformedParticipants}
                headers={headers}
                filename="participants.csv"
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handlePrintClick}
                color="primary"
                disabled={!transformedParticipants?.length}
              >
                <PrintIcon />
              </IconButton>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <Grid item>
              <CSVLinkWrapper
                data={transformedParticipants}
                headers={headers}
                filename="participants.csv"
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handlePrintClick}
                color="primary"
                disabled={!transformedParticipants?.length}
              >
                <PrintIcon />
              </IconButton>
            </Grid>
          </Box>
        )}
      </>
    );
  };

  const renderAddButton = () => {
    return (
      <>
        {canAddParticipant && (
          <Grid item>
            <PATAddButton
              tooltipText="Add Training Participant"
              onClick={onAddClick}
            />
          </Grid>
        )}
      </>
    );
  };

  const renderClearButton = () => {
    return (
      <>
        <Grid item>
          <Grid item>
            <PATClearButton title="Clear Filters" onClick={resetFilters} />
          </Grid>
        </Grid>
      </>
    );
  };

  const filterFields = [
    {
      type: "date",
      label: "Course Date From",
      field: "courseDateFrom",
      xs: 2.4,
    },
    { type: "date", label: "Course Date To", field: "courseDateTo", xs: 2.4 },
    {
      type: "autocomplete",
      label: "Filter By Time",
      field: "filterByTime",
      xs: 2.4,
      options: filterByTimeOptions,
      multiselect: false,
    },
    {
      type: "autocomplete",
      label: "Employee",
      field: "employeeNum",
      xs: 2.4,
      options: employeeOptions,
    },
    {
      type: "autocomplete",
      label: "Course",
      field: "course",
      xs: 2.4,
      options: courseOptions,
    },
    {
      type: "autocomplete",
      label: "Course Location",
      field: "courseLocation",
      xs: 3,
      options: locationOptions,
    },
    {
      type: "autocomplete",
      label: "Tags",
      field: "tagTypes",
      options: tagTypeOptions,
      xs: 3,
    },
    {
      type: "autocomplete",
      label: "Filter Field",
      field: "filterField",
      xs: 3,
      options: filterFieldOptions,
      multiselect: false,
    },
    {
      type: "text",
      label: "Includes the value",
      field: "filterValue",
      xs: 3,
    },
    {
      type: "checkbox",
      label: "Approved",
      field: "approved",
      xs: 2.8,
    },
    {
      type: "checkbox",
      label: "Check Sent",
      field: "checkSent",
      xs: 2.8,
    },
    {
      type: "checkbox",
      label: "View Pending Courses",
      field: "viewPendingCourses",
      xs: 2.8,
    },
    {
      type: "checkbox",
      label: "View Courses Pending Registration",
      field: "viewCoursesPendingRegistration",
      xs: 2.8,
    },
  ];

  return (
    <>
      <Typography sx={{ fontSize: 20 }}>Training Records</Typography>
      <FilterToolbar
        isSmall={isSmall}
        canCreate={canAddParticipant}
        leftSection={
          !isSmall && (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: "10px",
                padding: 2,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 1,
              }}
            >
              {renderAddButton()}
            </Box>
          )
        }
        middleSection={
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
              borderRadius: "10px",
              padding: 2,
              maxHeight: { xs: "25vh", md: "40vh" },
              overflowY: "auto",
              marginLeft: canAddParticipant ? "" : 1.5,
            }}
          >
            <Grid container spacing={1}>
              {renderFilterFields([...filterFields])}
              {renderClearButton()}
              <FilterButtons
                onSelectFilterClick={() => setOpenSelectFilterDialog(true)}
                onSaveFilterClick={() => setOpenSaveFilterDialog(true)}
                isFilterEmpty={isFilterEmpty}
              />
            </Grid>
          </Box>
        }
        rightSection={
          !isSmall && (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: "10px",
                padding: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                height: "100%",
                justifyContent: "center",
                marginRight: 1,
              }}
            >
              {renderOptions()}
            </Box>
          )
        }
        combinedActions={
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {renderAddButton()}
            {renderOptions()}
          </Box>
        }
        leftGridProps={{ xs: 12, md: 1 }}
        middleGridProps={{ xs: 12, md: 10 }}
        rightGridProps={{ xs: 12, md: 1 }}
      />

      <PrintsSearch3
        title="Participants"
        data={transformedParticipants}
        cols={columnsForPrint}
        kbdKey={kbdKey}
        print={printFlag}
      />

      <SelectFilterDialog
        open={openSelectFilterDialog}
        onClose={() => setOpenSelectFilterDialog(false)}
        screenName={screenNames.participants}
        onFilterChange={onFilterChange}
      />

      <SaveFilterDialog
        open={openSaveFilterDialog}
        onClose={() => setOpenSaveFilterDialog(false)}
        screenName={screenNames.participants}
        filters={localFilters}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  keyboard: state.keyboard,
  ...ownProps,
});

export default connect(mapStateToProps)(ParticipantTableToolbar);
